// SAB-1192: what week the app should display for an NFL league.
//
// ── THE OUTAGE THIS FIXES ────────────────────────────────────────────────────
//
// On 2026-09-15, with every week 1 game final and current_week correctly advanced
// to 2 by nfl-matchup-engine, every in-season league still showed "Week 1 LIVE"
// and every player was locked. Nobody could set a week 2 lineup.
//
// The clamp in NFLLeagueContext read:
//
//     const currentWeek = lastCompletedWeek !== null && rawWeek > lastCompletedWeek
//       ? lastCompletedWeek : rawWeek;
//
// with lastCompletedWeek = max(nfl_matchups.week) WHERE status = 'completed'.
//
// Its comment said "default to last completed week if current_week exceeds available
// data". The intent is right; the implementation tested something else. THE LAST
// COMPLETED WEEK IS BY DEFINITION IN THE PAST, so the moment a week completes the
// clamp pins the UI to it and holds until the NEXT week completes. It fires every
// week, forever, and leaves the app permanently one week behind. 2026 week 1 was
// simply the first completed week of the season, so it was the first time it showed.
//
// The damage was not cosmetic. NFLMyTeamPage feeds this value to the roster query,
// to the lineup LOCK (which resolves games for that week -- all of week 1's had
// kicked off, so everything locked), and to the lineup WRITES. A manager who got
// past the lock would have written to week 1.
//
// ── THE RULE ─────────────────────────────────────────────────────────────────
//
// "Available data" means a week the league HAS A SCHEDULE FOR, not one it has
// finished playing. Week 2 had 16 scheduled NFL games and 6 league matchups; it was
// available in every sense that matters. So clamp against the furthest scheduled
// week instead -- which is 14 for every in-season league, so no real league clamps,
// while a league whose pointer genuinely runs past its schedule still does.

/**
 * The week to display.
 *
 * @param rawWeek            nfl_leagues.current_week
 * @param maxScheduledWeek   max(nfl_matchups.week) for the league, ANY status.
 *                           null means "not loaded yet, or no schedule" -- in which
 *                           case DO NOT CLAMP. Clamping on absent data is what
 *                           produced the outage; an unknown ceiling is not a low one.
 */
export function clampWeekToSchedule(
  rawWeek: number,
  maxScheduledWeek: number | null,
): number {
  if (maxScheduledWeek === null || !Number.isFinite(maxScheduledWeek)) return rawWeek;
  return rawWeek > maxScheduledWeek ? maxScheduledWeek : rawWeek;
}

/**
 * The ceiling to store for a league, given its schedule query result.
 *
 * THIS FUNCTION EXISTS TO MAKE THE MISSING `else` IMPOSSIBLE. The old effect was:
 *
 *     if (data?.length) { setLastCompletedWeek(data[0].week); }   // no else
 *
 * On a league with no matching rows it simply did not call the setter, so React kept
 * the PREVIOUS league's value and clamped the new league to a week it had never
 * played. Expressing the decision as a total function -- one that always returns a
 * value, including null -- removes the shape that allowed the bug: there is no path
 * through it that means "keep whatever was there before".
 */
export function scheduleCeilingFromRows(
  rows: { week: number }[] | null | undefined,
): number | null {
  return rows?.length ? rows[0].week : null;
}
